import {API_ENDPOINT} from '../config/settings';
import {createApi} from '@reduxjs/toolkit/query/react';
import {axiosBaseQuery} from './utils/axios-base-query';
import userEndpoints, {type as userType} from './userEndpoints';
import roleEndpoints, {type as roleType} from './roleEndpoints';
import rotationEndpoints, {type as rotationType} from './rotationEndpoints';
import rulesEndpoints, {type as ruleType} from './rulesEndpoints';
import movementEndpoints, {type as movementType} from './movementEndpoints';
import recordEndpoints, {type as recordType} from './recordEndpoints';

export const api = createApi({
  reducerPath: 'ops360API',
  baseQuery: axiosBaseQuery({baseUrl: API_ENDPOINT}),
  tagTypes: [userType, roleType, rotationType, ruleType, movementType, recordType],
  keepUnusedDataFor: 30,
  endpoints: (builder) => ({
    ...userEndpoints(builder),
    ...roleEndpoints(builder),
    ...rotationEndpoints(builder),
    ...movementEndpoints(builder),
    ...rulesEndpoints(builder),
    ...recordEndpoints(builder)
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  /** @type {(params?: {pattern?: string}) => RTKQuery<User[]>} */
  useGetAllUsersQuery,
  /** @type {(params?: {page?: number, pattern?: string}) => RTKQuery<PaginatedResponse<User>>} */
  useGetUsersQuery,
  /** @type {(params?: {employeeId?: string}) => RTKQuery<User>} */
  useGetUserQuery,
  /** @type {() => [(user: AddUserDTO) => Promise, RTKQuery<User>]} */
  useAddUserMutation,
  /** @type {() => [(user: User) => Promise, RTKQuery<ServerMutationResponse>]} */
  useDeleteUserMutation,
  /** @type {() => [(user: AddUserDTO) => Promise, RTKQuery<User>]} */
  useEditUserMutation,
  /** @type {() => RTKQuery<CurrentUser>} */
  useGetCurrentUserQuery,
  /** @type {() => [(pattern: string) => void, RTKQuery<User[]>]} */
  useGetAzureUsersMutation,

  /** @type {(params?: {page?: number}) => RTKQuery<PaginatedResponse<Role>>} */
  useGetRolesQuery,
  /** @type {() => RTKQuery<Role[]>} */
  useGetAllRolesQuery,
  /** @type {(params?: {roleId?: string}) => RTKQuery<Role>} */
  useGetRoleQuery,
  /** @type {() => [(name: string, description?: string, users?: EmployeeId[]) => Promise, RTKQuery<Role>]} */
  useCreateRoleMutation,
  /** @type {() => [(name: string, description?: string, users?: EmployeeId[]) => Promise, RTKQuery<Role>]} */
  useUpdateRoleMutation,
  /** @type {() => [(role: Role) => Promise, RTKQuery<ServerMutationResponse>]} */
  useDeleteRoleMutation,

  /** @type {(params: {iataCode: IataCode}) => RTKQuery<Rotation[]>} */
  useGetRotationsQuery,
  /** @type {(arg: {flightDirection: FlightDirection, body: StationBriefingDTO}) => RTKQuery<StationBriefing>} */
  useGetStationBriefingQuery,

  /** @type {() => RTKQuery<string>} */
  useGetRulesQuery,
  /** @type {() => [(file: File) => void, RTKQuery<string>]} */
  useUploadRulesMutation,
  /** @type {() => RTKQuery<string>} */
  useGetRulesYamlMutation,

  /** @type {() => [(arg: {body: MovementAtaDTO, params?: {preview: (0|1)}}) => void, RTKQuery<MVTPreviewArrival>]} */
  useCreateAtaMutation,
  /** @type {() => [(arg: {body: MovementEtdDTO, params?: {preview: (0|1)}}) => void, RTKQuery<MVTPreviewETD>]} */
  useCreateEtdMutation,
  /** @type {() => [(arg: {body: MovementAtdDTO, params?: {preview: (0|1)}}) => void, RTKQuery<MVTPreviewDeparture>]} */
  useCreateAtdMutation,
  /** @type {() => [(arg: {body: MovementDelaysDTO, params?: {preview: (0|1)}}) => void, RTKQuery<MVTPreviewDelay>]} */
  useCreateDelaysMutation,

  /** @type {(params: {rotationHash: RotationHash}) => RTKQuery<Rotation[]>} */
  useGetRecordsQuery,
  /** @type {(params: {rotationHash: RotationHash}) => RTKQuery<Record[]>} */
  useGetOptionalActionsQuery,
  /** @type {() => [(params: {rotationHash: RotationHash}, body: {action: Action | ActionCheckList}) => void, RTKQuery<Record>]} */
  useAddOptionalActionMutation,
  /** @type {() => [(params: {rotationHash: RotationHash, recordId: RecordId}) => void, RTKQuery<Rotation>]} */
  useRemoveActionMutation,
  /** @type {() => [(arg: {body: {action: ActionCheckList | Action}, params?: {recordId: RecordId}}) => void, RTKQuery<Record>]} */
  useUpdateActionMutation,
  /** @type {() => [(arg: {body: [{recordId: RecordId, applicable: Boolean}]}) => void, RTKQuery<Record>]} */
  useApplicableRecordsMutation,
  /** @type {() => [(arg: {recordId: RecordId}) => void, RTKQuery<Record>]} */
  useStartActionMutation,
  /** @type {() => [(arg: {recordId: RecordId}) => void, RTKQuery<Record>]} */
  useFinishActionMutation,
  /** @type {() => [(arg: {recordId: RecordId}) => void, RTKQuery<Record>]} */
  useUnStartActionMutation,
  /** @type {() => [(arg: {recordId: RecordId}) => void, RTKQuery<Record>]} */
  useUnFinishActionMutation,
  /** @type {() => [(arg: body: UpdateRecordDTO, params?: {recordId: RecordId}) => void, RTKQuery<Record>]} */
  useUpdateRecordMutation,
  /** @type {() => [(arg: {body: {belt: String, stand: String, gate: String}, hash: String}) => void, RTKQuery<Record>]} */
  useUpdateSandGateBeltMutation,

  endpoints,
  util
} = api;

export default api;
